import React, { PureComponent } from "react";
import { find, map, zip, fromPairs, isNil } from "lodash";
import { Loader } from "react-loaders";

import Teams from "api/Teams";
import Auth from "routes/Auth";
import { getRequest, batchRequest } from "api/ApiService";
import { logError } from "services/utils";
import GreetingsText from "./GreetingsText";
import HowTeamDid from "./HowTeamDid";
import TeamDecisions from "./TeamDecisions";
import FinancialTargets from "./FinancialTargets";

const APICalls = (roundId, competitionId, courseId) => [
  //{ url: 'static/constants', key: 'constants', round: false },
  { url: "team/page/financials", key: "team", fn: body => body.team },
  {
    url: "team/grading",
    key: "grades",
    fn: body => (body.length === 0 ? {} : body[0])
  },
  {
    url: "competition/competitive_analysis",
    key: "competitiveAnalysis",
    zeroItem: false
  },
  {
    url: "competition/competitive_analysis",
    key: "competitiveAnalysisLte",
    round: "lte",
    zeroItem: false
  },
  { url: "teams", key: "teams", zeroItem: false },
  {
    url: `game/rounds/${roundId}/instructor_dashboard/?competition_id=${competitionId}`,
    key: "instructorData",
    zeroItem: false,
    round: false
  },
  {
    url: "teams/unaccepted_decisions",
    key: "unaccepted_decisions",
    zeroItem: false
  },
  { url: "team/products", key: "products", zeroItem: false },
  { url: "team/price_forecasts", key: "forecasts", zeroItem: false },
  { url: "team/promos", key: "promos", zeroItem: false },
  { url: "team/sales", key: "sales", zeroItem: false },
  {
    url: "v2/games/teamgradings/",
    key: "teamGradings",
    zeroItem: false,
    round: false
  },
  {
    url: `v2/games/targets/?course=${courseId}`,
    key: "financialTargets",
    zeroItem: false,
    round: false
  }
];

export default class Dashboard extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      team: {},
      grades: null,
      competitiveAnalysis: null,
      projections: [],
      products: [],
      forecasts: [],
      promos: [],
      sales: [],
      financials: {},
      unaccepted_decisions: null,
      teams: {},
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      instructorData: null
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  getCurrentCourseId = () => {
    return Auth.getCourseId();
  };

  fetchData = () => {
    const round = this.getRound();
    if (this.props.rounds.length === 0) {
      return;
    }
    const competitionId = Teams.currentTeam().competition;
    const roundData = find(this.props.rounds, r => r.number === round);
    const roundId = (roundData || {}).id;
    const courseId = this.getCurrentCourseId();
    const apiCalls = APICalls(roundId, competitionId, courseId);
    this.setState({
      competitiveAnalysisLte: null,
      instructorData: null
    });
    batchRequest(
      map(
        apiCalls,
        call =>
          `${process.env.REACT_APP_API_URL}${call.url}` +
          {
            false: () => "",
            undefined: () => `/?round_number=${round}`,
            lte: () => `/?round_lte=${round}`
            //'lteFinancial': () => `/?round_lte=${roundFinancial}`,
          }[call.round]()
      )
    ).then(res => {
      const newState = fromPairs(
        map(zip(res, apiCalls), ([{ body }, { key, zeroItem, fn }]) => {
          return [
            key,
            fn ? fn(body) : !isNil(zeroItem) && !zeroItem ? body : body[0]
          ];
        })
      );
      this.setState(newState);
    });
  };

  componentWillReceiveProps(nextProps) {
    const { rounds } = this.props;
    const nextRounds = nextProps.rounds;
    const roundsAreGood = !isNil(rounds) && rounds.length > 0;
    const nextRoundsAreGood = !isNil(nextRounds) && nextRounds.length > 0;
    if (
      nextProps.currentRound !== this.state.currentRound ||
      nextProps.roundInUrl !== this.state.roundInUrl ||
      (!roundsAreGood && nextRoundsAreGood)
    ) {
      this.setState(
        {
          currentRound: nextProps.currentRound,
          roundInUrl: nextProps.roundInUrl
        },
        () => {
          this.fetchData();
        }
      );
    }
  }

  getRound = () => {
    const { roundInUrl, currentRound } = this.state;
    return roundInUrl !== null ? roundInUrl : currentRound;
  };

  withRound = url =>
    `${process.env.REACT_APP_API_URL}${url}/?round_number=${this.getRound()}`;

  fetchInstructorData = roundId => {
    const url = `${process.env.REACT_APP_API_URL}game/rounds/${roundId}/instructor_dashboard/`;
    const request = getRequest(url);
    request.then(({ body }) => {
      this.setState({
        instructorData: body
      });
    });
  };

  fetchUnacceptedDecisions = () => {
    const url = this.withRound("teams/unaccepted_decisions");

    if (this.unacceptedDecisionsRequest) {
      this.unacceptedDecisionsRequest.abort();
      this.unacceptedDecisionsRequest = null;
    }
    this.unacceptedDecisionsRequest = getRequest(url);

    this.unacceptedDecisionsRequest
      .then(({ body }) => {
        this.setState({
          unaccepted_decisions: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchUnacceptedDecisions");
      });
  };

  fetchBalance = () => {
    getRequest(this.withRound("team/page/financials"))
      .then(({ body: { team } }) => {
        this.setState({
          team
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchBalance");
      });
  };

  fetchTeamGrades = () => {
    const url = this.withRound("team/grading");

    this.teamGradeRequest = getRequest(url);

    this.teamGradeRequest
      .then(({ body }) => {
        this.setState({
          grades: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchTeamGrades");
      });
  };

  fetchCompetitiveAnalysis = () => {
    const url = this.withRound("competition/competitive_analysis");

    if (this.teamCompetitiveAnalysisRequest) {
      this.teamCompetitiveAnalysisRequest.abort();
      this.teamCompetitiveAnalysisRequest = null;
    }
    this.teamCompetitiveAnalysisRequest = getRequest(url);

    this.teamCompetitiveAnalysisRequest
      .then(({ body }) => {
        this.setState({
          competitiveAnalysis: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchCompetitiveAnalysis");
      });
  };

  fetchProjections = () => {
    const url = this.withRound("team/projections");

    if (this.projectionsRequest) {
      this.projectionsRequest.abort();
      this.projectionsRequest = null;
    }
    this.projectionsRequest = getRequest(url);

    this.projectionsRequest
      .then(({ body }) => {
        this.setState({
          projections: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchProjections");
      });
  };

  fetchProducts = () => {
    const url = this.withRound("team/products");

    if (this.productsRequest) {
      this.productsRequest.abort();
      this.productsRequest = null;
    }
    this.productsRequest = getRequest(url);

    this.productsRequest
      .then(({ body }) => {
        this.setState({
          products: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchProducts");
      });
  };

  fetchForecasts = () => {
    const url = this.withRound("team/price_forecasts");

    if (this.forecastsRequest) {
      this.forecastsRequest.abort();
      this.forecastsRequest = null;
    }
    this.forecastsRequest = getRequest(url);

    this.forecastsRequest
      .then(({ body }) => {
        this.setState({
          forecasts: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchForecasts");
      });
  };

  fetchPromos = () => {
    const url = this.withRound("team/promos");

    if (this.promosRequest) {
      this.promosRequest.abort();
      this.promosRequest = null;
    }
    this.promosRequest = getRequest(url);

    this.promosRequest
      .then(({ body }) => {
        this.setState({
          promos: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchPromos");
      });
  };

  fetchSales = () => {
    const url = this.withRound("team/sales");

    if (this.salesRequest) {
      this.salesRequest.abort();
      this.salesRequest = null;
    }
    this.salesRequest = getRequest(url);

    this.salesRequest
      .then(({ body }) => {
        this.setState({
          sales: body
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchSales");
      });
  };

  fetchFinancials = () => {
    const url = this.withRound("team/page/financials");

    if (this.financialsRequest) {
      this.financialsRequest.abort();
      this.financialsRequest = null;
    }
    this.financialsRequest = getRequest(url);

    this.financialsRequest
      .then(({ body: { team } }) => {
        this.setState({
          financials: team
        });
      })
      .catch(err => {
        logError(err, "Dashboard", "fetchFinancials");
      });
  };

  render() {
    const {
      team,
      competitiveAnalysisLte,
      grades,
      instructorData,
      unaccepted_decisions,
      products,
      forecasts,
      promos,
      sales,
      financialTargets,
      teamGradings
    } = this.state;
    const { team_grade, rounds } = this.props;

    if (
      rounds.length === 0 ||
      isNil(competitiveAnalysisLte) ||
      isNil(instructorData) ||
      isNil(grades) ||
      isNil(instructorData)
    ) {
      return <Loader type="ball-scale-ripple" active />;
    }
    const { first_name, last_name, role } = Auth.getUser();
    const round = this.getRound();
    const isFinished = find(rounds, rnd => rnd.number === round)["is_finished"];
    const gradeForText = grades.ranking;

    return (
      <div>
        <div className="dashboard">
          {!isFinished ? (
            <div className="dashboard-body">
              <GreetingsText
                team_grade={team_grade}
                data={gradeForText}
                round={round}
                isFinished={isFinished}
                role={role}
                first_name={first_name}
                last_name={last_name}
              />
              <FinancialTargets
                round={round}
                financialTargets={financialTargets}
              />
              <TeamDecisions
                unaccepted_decisions={unaccepted_decisions}
                products={products}
                forecasts={forecasts}
                promos={promos}
                sales={sales}
                team={team}
                round={round}
                role={role}
              />
            </div>
          ) : (
            <div className="dashboard-body">
              <GreetingsText
                team_grade={team_grade}
                data={gradeForText}
                round={round}
                isFinished={isFinished}
                role={role}
                first_name={first_name}
                last_name={last_name}
              />
              <HowTeamDid round_number={round} teamGradings={teamGradings} />
              <FinancialTargets
                round={round}
                financialTargets={financialTargets}
              />
              <TeamDecisions
                unaccepted_decisions={unaccepted_decisions}
                products={products}
                forecasts={forecasts}
                promos={promos}
                sales={sales}
                team={team}
                round={round}
                role={role}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/dashboard/Dashboard.js